import { Injectable, inject } from '@angular/core';
import { Location } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Params, Router as RouterNg, UrlTree } from '@angular/router';
import { filter, map, mergeMap, pairwise, startWith } from 'rxjs';

@Injectable({
	providedIn: 'root'
})
export class Router{
	readonly router=inject(RouterNg);
	readonly route=inject(ActivatedRoute);
    private location=inject(Location);

	readonly previousUrlTree$=this.router.events.pipe(
	    filter((e): e is NavigationEnd => e instanceof NavigationEnd),
	    map(e => e.urlAfterRedirects),
	    startWith(this.router.url),
	    pairwise(),
	    map(([prev]) => this.router.parseUrl(prev)) // => UrlTree avec query + fragment
	);

	readonly previousUrlTree=toSignal<UrlTree | null>(this.previousUrlTree$, { initialValue: null });

	//retourne la dernière url visité
	//on peut ajouter des queryParam et/ou un fragment
	getPrevious(queryParams: Params={}, fragment?: string){
		const prev=this.previousUrlTree();
		let tree: UrlTree=null;
		if(prev){
		  tree=this.router.parseUrl(this.router.serializeUrl(prev));

		  // merge query params
		  tree.queryParams={ ...tree.queryParams, ...queryParams };

		  // set/override fragment si fourni
		  if(fragment!==undefined) 
		  	tree.fragment=fragment;
		}
		
		return tree;
	}

	//modifie un queryParam et/ou un fragment dans l'url courant sans recharger le composant
	//sans recharger le composant
	setUrlAttribute(queryParams: Params={}, fragment: string=this.route.snapshot.fragment){
	    this.router.navigate([], {
	      relativeTo: this.route,           // reste sur la même route
	      queryParams: queryParams,         // ajoute/modifie
	      queryParamsHandling: 'merge',     // garde les autres params existants
	      replaceUrl: true,                 // optionnel: évite de polluer l’historique
	      fragment: fragment  				// fragment
	    });
	}

	//supprime le fragment de l'url courant sans recharger le composant
	removeFragment(){
		//this.setUrlFragment(null);
		const tree=this.router.parseUrl(this.router.url);
		tree.fragment=null;
		this.location.replaceState(tree.toString()); // garde path + query params, enlève juste #
	}

	//supprime les queryParams 
	//sans recharger le composant
	removeQueryParams(){
		this.setUrlAttribute({});
	}

	//ajout un fragment
	//sans recharger le composant
	setUrlFragment(fragment: string){
		this.setUrlAttribute(this.route.snapshot.queryParams, fragment);
	}

	data$(){
		return this.router.events.pipe(
	      filter(event => event instanceof NavigationEnd),
	      map(() => {
	        let route=this.route.firstChild;
	        let child=route;
	        while(child){
	          if(child.firstChild){
	            child=child.firstChild;
	            route=child;
	          }else
	            child=null;
	        }
	        return route;
	      }),
	      mergeMap(route => route.data)
	    );
	}

	//retourne l'uri d'une route qui porte le nom : name
	get(name: string, addRoot: boolean=true){
		let result: string=null;
		this.router.config.forEach((route: any) => {
			let path: string=this.getReccurs(name, route, null);
			if(path!=null)
				result=path;
		});

		if(addRoot)
			result='/'+result;
		return result;
	}

	private getReccurs(name: string, route: any, path: string){
		let currentPath: string=((path!=null)? path+"/"+route.path : route.path);
		let result: string=null;
		if(route.data || route.children){
			if(!route.children && route.data && route.data.name==name)
				result=currentPath;
			else if(route.children){
				let final: string=null;
				route.children.forEach((child: any) => {
					if(!final)
						final=this.getReccurs(name, child, currentPath);
				});
				result=final;
			}
			else
				result=null;
		}

		return result;
	}

	//méthode qui retourne l'uri avec un parse des param
	parse(name: string, params: string[]=[], addRoot: boolean=true){
		let result: string=null;
		let path: string=this.get(name, addRoot);
		if(path){
			let framgents: string[]=path.split('/');

			let ind: number=0;
			result=framgents.map((e, i) => {
				let frag: string=e;
				if(e.startsWith(':')){
					frag=params[ind];
					ind++;
				}
				return frag;
			}).join('/');
		}

		return result;
	}

	//méthode qui navigue vers une url
	navigate(name: string, params: string[]=[], queryParams: any={}){
		let path: string=this.parse(name, params);
		if(path)
			this.router.navigate([path], {queryParams});
	}

	navigateByUrl(url: string){
		this.router.navigateByUrl(url);
    }

}